import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { fetchSections, fetchEntries, fetchAnniversaryStatus } from "@/lib/api";
import { TID } from "@/lib/testIds";
import { BookOpen, ArrowUpRight, ChevronLeft, Lock, Sparkles, Download, CreditCard, Users, Settings as SettingsIcon, BookMarked, FileText, NotebookPen, Library as LibraryIcon, Newspaper, Mic } from "lucide-react";
import UserBadge from "@/components/UserBadge";
import AdminToggle from "@/components/AdminToggle";
import GiftInbox from "@/components/GiftInbox";
import ReferralProgress from "@/components/ReferralProgress";
import ManageSubscriptionModal from "@/components/ManageSubscriptionModal";
import StreakRibbon from "@/components/StreakRibbon";
import SeasonMicroPrompt from "@/components/SeasonMicroPrompt";
import RerunCard from "@/components/RerunCard";
import ShareUmbraCard from "@/components/ShareUmbraCard";
import AnniversaryPortraitCard from "@/components/AnniversaryPortraitCard";
import TrialBanner from "@/components/TrialBanner";
import { useAuth } from "@/lib/auth";
import { useEntitlements } from "@/lib/entitlements";
import { useSeason } from "@/lib/season";
import { exportChapterPdf, exportFullArchivePdf } from "@/lib/pdf";
import { toast } from "sonner";

export default function Dashboard() {
  const [sections, setSections] = useState([]);
  const [countsByS, setCountsByS] = useState({});
  const [loading, setLoading] = useState(true);
  const [anniversary, setAnniversary] = useState({ eligible: false });
  const { user } = useAuth();
  const navigate = useNavigate();
  const { entitlements, chapterUnlocked } = useEntitlements();
  const [allEntries, setAllEntries] = useState([]);
  const season = useSeason();

  // Return the user to the blog post they were reading before the paywall
  // triggered the Google sign-in round trip.
  useEffect(() => {
    if (!user) return;
    try {
      const target = window.sessionStorage.getItem("umbra_post_return");
      if (target && target.startsWith("/blog/")) {
        window.sessionStorage.removeItem("umbra_post_return");
        navigate(target, { replace: true });
      }
    } catch {
      /* sessionStorage disabled */
    }
  }, [user, navigate]);

  useEffect(() => {
    // Mount-only fetch — each request is resolved independently so a
    // single 401/502 (e.g. expired entries session) can never wipe out
    // the sections grid.
    (async () => {
      const [secsR, entriesR, annivR] = await Promise.allSettled([
        fetchSections(),
        fetchEntries(),
        fetchAnniversaryStatus(),
      ]);
      if (secsR.status === "fulfilled") {
        setSections(secsR.value);
      } else {
        console.error("fetchSections failed", secsR.reason);
      }
      const entries = entriesR.status === "fulfilled" ? entriesR.value : [];
      if (entriesR.status !== "fulfilled") {
        console.warn("fetchEntries failed", entriesR.reason);
      }
      setAllEntries(entries);
      setAnniversary(annivR.status === "fulfilled" ? annivR.value : { eligible: false });
      const counts = {};
      entries.forEach((e) => {
        counts[e.section_id] = (counts[e.section_id] || 0) + 1;
      });
      setCountsByS(counts);
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleExportAll = () => {
    if (allEntries.length === 0) {
      toast.info("Save an entry first — your archive is quiet.");
      return;
    }
    try {
      exportFullArchivePdf({ sections, entries: allEntries });
      toast.success("Your archive has been prepared.");
    } catch (e) {
      console.error(e);
      toast.error("Could not export the archive.");
    }
  };

  const firstName = user?.name?.split(" ")[0] || "friend";
  const sub = entitlements?.subscription_active;
  const [manageOpen, setManageOpen] = useState(false);

  const visibleSections = useMemo(
    () => sections.filter((s) => s.id !== "anniversary" || anniversary.eligible),
    [sections, anniversary.eligible]
  );

  return (
    <div className="min-h-screen relative">
      <TrialBanner />
      <div className="max-w-6xl mx-auto px-6 sm:px-8 md:px-16 pt-10 sm:pt-16 md:pt-24 pb-32 relative z-10">
        <div className="flex items-center justify-between mb-10 sm:mb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 min-h-[44px] -mx-2 px-2 sm:min-h-0 sm:mx-0 sm:px-0 font-sans text-xs uppercase tracking-[0.24em] text-bone-400 hover:text-bone-200 transition-colors"
            data-testid={TID.backToDashboard}
          >
            <ChevronLeft className="h-3 w-3" /> Umbra
          </Link>
          <div className="flex items-center gap-3">
            <AdminToggle />
            <UserBadge />
          </div>
        </div>

        <div className="flex items-end justify-between mb-6 flex-wrap gap-4 sm:gap-6">
          <div>
            <p className="font-sans uppercase tracking-[0.24em] sm:tracking-[0.28em] text-xs text-terracotta mb-3 sm:mb-4">
              {user ? (
                <>Welcome back · <span className="text-bone-400">{firstName}</span></>
              ) : (
                "The Workbook"
              )}
            </p>
            <h1
              data-testid={TID.dashboardTitle}
              className="font-serif text-3xl sm:text-5xl lg:text-6xl text-bone-100 max-w-2xl leading-tight"
            >
              Seven chapters. <span className="italic font-light text-bone-300">One breath at a time.</span>
            </h1>
          </div>
          <div className="flex flex-col items-start sm:items-end gap-3 w-full sm:w-auto">
            {/* Nav row — icon + label on every viewport. Labels never rely on hover
                to appear (WCAG 2.4.4 Link Purpose). Mobile stays compact via
                small text + horizontal scroll if it overflows. */}
            <nav aria-label="Workbook navigation" className="flex items-center gap-3 sm:gap-4 flex-wrap">
              <Link
                to="/history"
                data-testid={TID.historyLink}
                aria-label="Your entries"
                title="Your entries"
                className="font-sans uppercase tracking-[0.2em] text-terracotta hover:text-bone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 transition-colors inline-flex items-center gap-2 px-3 py-2 sm:px-2 sm:py-1 border border-terracotta/40 sm:border-0 text-[0.7rem] sm:text-sm"
              >
                <NotebookPen className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                <span className="text-bone-200">Your entries</span>
              </Link>
              <Link
                to="/library"
                data-testid="library-link"
                aria-label="Ebook library"
                title="The Library"
                className="font-sans uppercase tracking-[0.2em] text-terracotta hover:text-bone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 transition-colors inline-flex items-center gap-2 px-3 py-2 sm:px-2 sm:py-1 border border-terracotta/40 sm:border-0 text-[0.7rem] sm:text-sm"
              >
                <LibraryIcon className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                <span className="text-bone-200">Library</span>
              </Link>
              <Link
                to="/blog"
                data-testid="journal-link"
                aria-label="The Journal — essays"
                title="The Journal"
                className="font-sans uppercase tracking-[0.2em] text-terracotta hover:text-bone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 transition-colors inline-flex items-center gap-2 px-3 py-2 sm:px-2 sm:py-1 border border-terracotta/40 sm:border-0 text-[0.7rem] sm:text-sm"
              >
                <Newspaper className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                <span className="text-bone-200">Journal</span>
              </Link>
              <Link
                to="/settings"
                data-testid="settings-link"
                aria-label="Voice settings"
                title="Voice settings"
                className="font-sans uppercase tracking-[0.2em] text-terracotta hover:text-bone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 transition-colors inline-flex items-center gap-2 px-3 py-2 sm:px-2 sm:py-1 border border-terracotta/40 sm:border-0 text-[0.7rem] sm:text-sm"
              >
                <Mic className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                <span className="text-bone-200">Voice</span>
              </Link>
              <button
                onClick={handleExportAll}
                data-testid="export-all-btn"
                aria-label="Export archive"
                title="Export archive"
                className="font-sans uppercase tracking-[0.2em] text-terracotta hover:text-bone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 transition-colors inline-flex items-center gap-2 px-3 py-2 sm:px-2 sm:py-1 border border-terracotta/40 sm:border-0 text-[0.7rem] sm:text-sm"
              >
                <Download className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                <span className="text-bone-200">Export</span>
              </button>
              {entitlements?.subscription_active && (
                <Link
                  to="/whisper"
                  data-testid="whisper-link"
                  aria-label="Whisper Circle"
                  title="Whisper Circle"
                  className="font-sans uppercase tracking-[0.2em] text-terracotta hover:text-bone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 transition-colors inline-flex items-center gap-2 px-3 py-2 sm:px-2 sm:py-1 border border-terracotta/40 sm:border-0 text-[0.7rem] sm:text-sm"
                >
                  <Users className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                  <span className="text-bone-200">Whisper Circle</span>
                </Link>
              )}
              <Link
                to="/keepsake"
                data-testid="keepsake-link"
                aria-label="Bound edition"
                title="Bound edition · Chapter 7 keepsake"
                className="font-sans uppercase tracking-[0.2em] text-terracotta hover:text-bone-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-terracotta focus-visible:ring-offset-2 focus-visible:ring-offset-obsidian-900 transition-colors inline-flex items-center gap-2 px-3 py-2 sm:px-2 sm:py-1 border border-terracotta/40 sm:border-0 text-[0.7rem] sm:text-sm"
              >
                <BookMarked className="h-5 w-5 sm:h-4 sm:w-4 shrink-0" strokeWidth={2} aria-hidden="true" />
                <span className="text-bone-200">Keepsake</span>
              </Link>
            </nav>
            {sub ? (
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-terracotta border border-terracotta/40 px-3 py-1" data-testid="subscription-active-badge">
                  <Sparkles className="h-3 w-3" /> Umbra Premium · active
                </span>
                <button
                  onClick={() => setManageOpen(true)}
                  data-testid="manage-subscription-btn"
                  className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-300 hover:text-terracotta transition-colors border-b border-transparent hover:border-terracotta pb-0.5"
                >
                  <CreditCard className="h-3 w-3" />
                  Manage
                </button>
              </div>
            ) : (
              <span className="inline-flex items-center gap-2 font-sans text-[0.7rem] uppercase tracking-[0.24em] text-bone-500" data-testid="private-space-indicator">
                <Lock className="h-3 w-3" /> Private space · signed in
              </span>
            )}
          </div>
        </div>

        <p className="font-sans text-sm text-bone-500 mb-8 max-w-lg">
          Your voice, your account. Two prompts of Chapter One are yours to explore before the paywall opens.
        </p>

        {season?.label && (
          <p className="font-sans text-[0.7rem] uppercase tracking-[0.28em] text-bone-500 mb-6" data-testid="season-label" style={{ color: season.accent }}>
            The season · {season.label}
          </p>
        )}

        <SeasonMicroPrompt />

        <RerunCard />

        <ShareUmbraCard />

        <StreakRibbon />

        <AnniversaryPortraitCard anniversary={anniversary} />

        <GiftInbox sections={sections} onClaimed={() => window.location.reload()} />
        <ReferralProgress />

        {loading ? (
          <p className="font-sans text-bone-400">Preparing your workbook…</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-6 gap-5 md:gap-6 auto-rows-[minmax(200px,auto)]">
            {visibleSections.map((s, idx) => {
              // Asymmetric bento layout
              const spans = [
                "md:col-span-4 md:row-span-2",
                "md:col-span-2",
                "md:col-span-2",
                "md:col-span-3",
                "md:col-span-3",
                "md:col-span-2",
                "md:col-span-4",
              ];
              const count = countsByS[s.id] || 0;
              const isAnniversary = s.id === "anniversary";
              const unlocked = isAnniversary ? anniversary.eligible : chapterUnlocked(s.id);
              const isFreeChapter = s.id === (entitlements?.free_chapter_id || "inner-child");
              const showLock = !unlocked && !isFreeChapter;
              // Trial-expired users get a warmer "Peek" pill on Chapter 2
              // instead of the generic lock, so they see it's their tease.
              const isChapter2Peek =
                showLock && s.id === "core-wounds" && Boolean(entitlements?.chapter_2_preview);
              return (
                <Link
                  key={s.id}
                  to={`/section/${s.id}`}
                  data-testid={TID.sectionCard(s.id)}
                  className={`group relative bg-obsidian-700 hover:bg-obsidian-600 border ${isAnniversary ? "border-terracotta/50" : "border-obsidian-600"} hover:border-terracotta/50 p-8 flex flex-col justify-between overflow-hidden transition-colors duration-500 ${spans[idx % spans.length]}`}
                >
                  <div
                    className="absolute -right-16 -top-16 h-56 w-56 rounded-full opacity-20 blur-3xl group-hover:opacity-40 transition-opacity"
                    style={{ backgroundColor: s.color }}
                    aria-hidden="true"
                  />
                  {isAnniversary && (
                    <div className="absolute top-6 right-6 z-10 font-sans text-[0.6rem] uppercase tracking-[0.24em] text-terracotta border border-terracotta/40 px-2 py-1" data-testid="anniversary-badge">
                      Anniversary
                    </div>
                  )}
                  {showLock && !isAnniversary && (
                    isChapter2Peek ? (
                      <div
                        className="absolute top-6 right-6 z-10 font-sans text-[0.6rem] uppercase tracking-[0.22em] text-terracotta border border-terracotta/60 bg-terracotta/10 px-2 py-1"
                        data-testid={`chapter-peek-${s.id}`}
                      >
                        Peek
                      </div>
                    ) : (
                      <div className="absolute top-6 right-6 z-10 h-8 w-8 border border-obsidian-600 group-hover:border-terracotta flex items-center justify-center transition-colors" data-testid={`chapter-lock-${s.id}`}>
                        <Lock className="h-3.5 w-3.5 text-bone-400 group-hover:text-terracotta transition-colors" />
                      </div>
                    )
                  )}
                  {unlocked && !entitlements?.subscription_active && (
                    <div className="absolute top-6 right-6 z-10 font-sans text-[0.6rem] uppercase tracking-[0.24em] text-terracotta border border-terracotta/40 px-2 py-1" data-testid={`chapter-unlocked-${s.id}`}>
                      Unlocked
                    </div>
                  )}
                  <div className="relative z-10">
                    <p className="font-sans uppercase tracking-[0.24em] text-[0.65rem] text-bone-400 mb-4">{s.subtitle}</p>
                    <h2 className="font-serif text-2xl md:text-3xl text-bone-100 mb-3 leading-tight">
                      {s.title}
                    </h2>
                    <p className="font-sans text-sm text-bone-400 leading-relaxed max-w-md">
                      {s.description}
                    </p>
                    {showLock && s.prompts?.[0]?.text && (
                      <div
                        className="mt-4 border-l-2 border-terracotta/50 pl-4"
                        data-testid={`chapter-preview-${s.id}`}
                      >
                        <p className="font-sans uppercase tracking-[0.24em] text-[0.55rem] text-terracotta mb-1">
                          A prompt from inside
                        </p>
                        <p className="font-serif italic text-sm text-bone-300 leading-relaxed max-w-md line-clamp-3">
                          "{s.prompts[0].text}"
                        </p>
                      </div>
                    )}
                  </div>
                  <div className="relative z-10 mt-6 flex items-end justify-between">
                    <div className="font-sans text-xs uppercase tracking-[0.2em] text-bone-500">
                      {s.prompts.length} prompts {count > 0 && <span className="text-terracotta">· {count} spoken</span>}
                    </div>
                    <ArrowUpRight className="h-5 w-5 text-bone-400 group-hover:text-terracotta group-hover:-translate-y-1 group-hover:translate-x-1 transition-all" />
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
      {manageOpen && (
        <ManageSubscriptionModal onClose={() => setManageOpen(false)} onPaused={() => window.location.reload()} />
      )}
    </div>
  );
}
